import { useEffect, useRef, useState } from "react";
import { api } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { refreshFamily, requireRefreshArray, useRefreshOwner } from "../../lib/useRefreshOwner";
import { notificationRows, type Activity, type ReadState } from "../../components/DashboardOverview";
import type { useDashboardState } from "./useDashboardState";

type Group = "all" | "current" | "mine" | "orders" | "reviews" | "disputes" | "profile" | "boosts";

/** Dashboard owns several independent projections. Keep current-view refresh
 * separate from commands and their smaller post-action rereads. */
export function useDashboardRefresh(state: ReturnType<typeof useDashboardState>, user: ReturnType<typeof useAuth>["user"], view: string) {
  const group = useRef<Group>("all");
  const clearAll = useRef(() => {});
  const [activity, setActivity] = useState<Activity[] | null>(null);
  const workerEnabled = user?.capabilities.some(capability => capability === "find_work" || capability === "offer_service");
  const refresh = useRefreshOwner(`${state.scope}:dashboard`, signal => {
    const datasets = {
      mine: refreshFamily(async () => {
        const response = await api.listMine(signal);
        for (const key of ["jobs", "projects", "services", "companies", "people", "rfqs"] as const) requireRefreshArray(response?.[key]);
        return response;
      }, response => { state.setMine(response); state.setMineScope(state.scope); state.setMineError(null); },
      () => { state.setMine({ jobs: [], projects: [], services: [], companies: [], people: [], rfqs: [] }); state.setMineScope(state.scope); }),
      companies: refreshFamily(() => api.myCompanies(signal), state.setCompanies, () => state.setCompanies([])),
      bids: refreshFamily(async () => requireRefreshArray((await api.myBids(signal))?.bids), state.setMyBids, () => state.setMyBids([])),
      quotes: refreshFamily(async () => requireRefreshArray((await api.myQuotes(signal))?.quotes), state.setMyQuotes, () => state.setMyQuotes([])),
      engagements: refreshFamily(() => api.engagements(signal), state.setEngagements, () => state.setEngagements([])),
      items: refreshFamily(() => api.myItems(signal), state.setMyItems, () => state.setMyItems([])),
      orders: refreshFamily(async () => {
        const response = await api.myOrders(signal);
        requireRefreshArray(response?.purchases); requireRefreshArray(response?.sales);
        return response;
      }, state.setOrders, () => state.setOrders({ purchases: [], sales: [] })),
      pending: refreshFamily(() => api.pendingReviews(signal), state.setPendingRevs, () => state.setPendingRevs([])),
      reviews: refreshFamily(async () => {
        const response = await api.myReviews(signal);
        requireRefreshArray(response?.received); requireRefreshArray(response?.written);
        if (!Number.isFinite(response.count)) throw new Error("Missing reviews.");
        return response;
      }, response => { state.setMyRevs(response); state.setReviewScope(state.scope); state.setReviewsFailed(false); },
      () => { state.setMyRevs(null); state.setReviewScope(state.scope); state.setReviewsFailed(true); }),
      disputes: refreshFamily(() => api.myDisputes(signal), state.setDisputes, () => state.setDisputes([])),
      boosts: refreshFamily(async () => {
        const response = await api.myBoosts(signal); requireRefreshArray(response?.boosts); return response;
      }, response => { state.setBoosts(response.boosts); state.setPaidPlansAvailable(response.paidPlansAvailable); },
      () => { state.setBoosts([]); state.setPaidPlansAvailable(false); }),
      profile: refreshFamily(async () => {
        if (!user) throw new Error("No signed-in profile.");
        const response = await api.getProfile(user.id, signal);
        if (!response?.user) throw new Error("Missing profile summary.");
        return response;
      }, response => { state.setProfile(response.user); state.setActiveResume("activeResume" in response ? response.activeResume : null); },
      () => { state.setProfile(null); state.setActiveResume(null); }),
      worker: refreshFamily(async () => {
        const response = await api.workerProfile(signal);
        if (!Number.isFinite(response?.completeness)) throw new Error("Missing setup summary.");
        return response;
      }, state.setWprofile, () => state.setWprofile(null)),
      credit: refreshFamily(async () => {
        const response = await api.getCredit(signal);
        if (!response || ![response.limit, response.outstanding, response.available].every(Number.isFinite)) throw new Error("Missing credit summary.");
        return response;
      }, data => state.setCreditRead({ scope: state.scope, data }), () => state.setCreditRead({ scope: state.scope, data: null })),
      activity: refreshFamily(async () => notificationRows(await api.getNotifications(signal)), setActivity, () => setActivity(null)),
    };
    clearAll.current = () => Object.values(datasets).forEach(dataset => dataset.clear());
    type Dataset = keyof typeof datasets;
    const groups: Record<Exclude<Group, "all" | "current">, Dataset[]> = {
      mine: ["mine", "companies"], orders: ["orders", "items", "pending"], reviews: ["pending", "reviews"],
      disputes: ["disputes", "orders"], profile: ["profile"], boosts: ["boosts"],
    };
    let selected: Dataset[];
    if (group.current === "all") selected = ["mine", "companies", "bids", "quotes", "engagements", "items", "orders", "pending", "reviews", "disputes", "boosts", "profile"];
    else if (group.current !== "current") selected = groups[group.current];
    else if (view) selected = view === "items" ? ["items"] : view === "bids" ? ["bids"] : view === "companies" ? ["companies"] : ["mine"];
    else {
      selected = ["profile", "mine", "companies"];
      const tab = state.isMobileViewport ? state.mobileTab : state.profileSection;
      if (tab === "listings") selected.push("boosts");
      if (tab === "work" || tab === "activity") selected.push("bids", "quotes", "engagements");
      if (tab === "orders" || tab === "activity") selected.push("orders", "items", "pending", "reviews", "disputes");
      if (tab === "reviews" || tab === "overview") selected.push("reviews", "pending");
    }
    if (!view && (group.current === "all" || group.current === "current")) {
      if (workerEnabled) selected.push("worker");
      if (state.isMobileViewport) { if (group.current === "all" || state.mobileTab === "orders") selected.push("credit"); }
      else selected.push("activity");
    } else if (group.current === "all" && workerEnabled) selected.push("worker");
    return [...new Set(selected)].map(key => datasets[key]);
  }, { enabled: !!user && user.userType !== "admin", auto: false, onProfileLost: () => clearAll.current() });
  function reloadGroup(next: Group) { group.current = next; return refresh.reload(); }
  useEffect(() => { if (user && user.userType !== "admin") void reloadGroup("all"); }, [state.scope, view, state.isMobileViewport, workerEnabled]);
  useEffect(() => { if (state.mineAttempt) void reloadGroup("mine"); }, [state.mineAttempt]);
  useEffect(() => {
    if (refresh.error && !state.mine) state.setMineError(refresh.error);
    if (refresh.error && !state.myRevs) state.setReviewsFailed(true);
  }, [refresh.error]);
  const activityState: ReadState<Activity[]> = { scope: state.scope, data: activity, status: activity ? "ready" : refresh.loaded ? "error" : "loading" };
  return { ...refresh, reloadGroup, activityState, onRefresh: (signal: AbortSignal) => { group.current = "current"; return refresh.onRefresh(signal); } };
}
